// CronDetail — drill-down run history for one cron (PX-104). Reached from
// Cron.tsx by clicking a cron name. Cursor-paginated over
// GET /admin/cron/{name}/history (newest first), gated by cron.read.

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import type { CronRunHistoryItem, CronRunHistoryResponse, CronStatus } from '@/types/api'

// ---------------------------------------------------------------------------
// Formatters (duplicated from Cron.tsx — small, and the admin has no shared
// date-util module; UserDetail/Generations each keep their own too).

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)}m`
}

/** Same per-cron "did the run change anything" heuristic as Cron.tsx, keyed
 *  by the cron name from the route. */
function hadChanges(name: string, item: CronRunHistoryItem): boolean {
  if (item.status !== 'success' || !item.summary) return false
  const s = item.summary as Record<string, unknown>
  switch (name) {
    case 'sync_recent_fal_models':
      return typeof s.upserted === 'number' && s.upserted > 0
    case 'cleanup_deleted_users':
      return typeof s.deleted === 'number' && s.deleted > 0
    default:
      return Object.values(s).some((v) => typeof v === 'number' && v > 0)
  }
}

function StatusBadge({ status }: { status: CronStatus }) {
  const color =
    status === 'success' ? 'bg-emerald-100 text-emerald-700' :
    status === 'error' ? 'bg-red-100 text-red-700' :
    'bg-yellow-100 text-yellow-700'  // running
  return <span className={`rounded-full px-2 py-0.5 text-xs ${color}`}>{status}</span>
}

// ---------------------------------------------------------------------------

export function CronDetailPage() {
  const { name = '' } = useParams()

  const [items, setItems] = useState<CronRunHistoryItem[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadPage = useCallback(
    async (cur: string | null) => {
      setLoading(true)
      setError(null)
      try {
        const qs = cur ? `?cursor=${encodeURIComponent(cur)}` : ''
        const resp = await request<CronRunHistoryResponse>(
          `/admin/cron/${encodeURIComponent(name)}/history${qs}`,
        )
        setItems((prev) => (cur ? [...prev, ...resp.items] : resp.items))
        setCursor(resp.cursor)
        setHasMore(resp.has_more)
      } catch (e) {
        setError(e instanceof APIError ? e.message : 'Failed to load run history')
      } finally {
        setLoading(false)
        setLoaded(true)
      }
    },
    [name],
  )

  // Initial page on mount / name change.
  useEffect(() => {
    setItems([])
    setCursor(null)
    setHasMore(true)
    setLoaded(false)
    loadPage(null)
  }, [loadPage])

  return (
    <div className="p-6">
      <header className="mb-4">
        <Link to="/cron" className="text-sm text-brand-600 hover:text-brand-700">
          ← Cron jobs
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          <code className="text-ink-900">{name}</code>
        </h1>
        <p className="text-sm text-ink-400">
          Run history (last 30 days), newest first. Rows come from the{' '}
          <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">cron_runs</code> table.
        </p>
      </header>

      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Changes</th>
              <th className="px-4 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-ink-50 last:border-b-0 align-top">
                <td className="px-4 py-3 text-xs text-ink-700">
                  {formatDateTime(item.started_at)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-700">
                  {formatDuration(item.started_at, item.finished_at)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={item.status} />
                </td>
                <td className="px-4 py-3">
                  {hadChanges(name, item) ? (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">yes</span>
                  ) : (
                    <span className="text-ink-400">no</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {item.status === 'error' && item.error_message ? (
                    <pre className="max-w-md whitespace-pre-wrap text-xs text-red-700">
                      {item.error_message}
                    </pre>
                  ) : item.summary && Object.keys(item.summary).length > 0 ? (
                    <pre className="max-w-md overflow-x-auto whitespace-pre-wrap text-xs text-ink-700">
                      {JSON.stringify(item.summary)}
                    </pre>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {loaded && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-ink-400">
                  No runs recorded yet for this cron.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && items.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => loadPage(cursor)}
            disabled={loading}
            className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      {loading && items.length === 0 && (
        <div className="mt-3 text-sm text-ink-400">Loading…</div>
      )}
    </div>
  )
}
